/* eslint-disable max-lines */
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  useArtwork,
  useArtworkForm,
  useArtworkImage,
  useCalculatedArtworkDimensions,
  useEnhancedImageSource
} from "@design-lab/entities/artwork";
import { useArtworkWithPolling } from "@design-lab/entities/artwork/model/useArtworkWithPolling";
import {
  useCompositionsActions,
  useCompositionsList,
  useCurrentComposition
} from "@design-lab/entities/composition";
import { useDesignProjectSync } from "@design-lab/features/design-project";
import { useArtworkSelection } from "@design-lab/features/weave-design-lab/model/useArtworkSelection";
import { toFileObject, type PlacementFormPosition } from "@design-lab/shared";
import { ARTWORK_PROCESSING } from "@design-lab/shared/constants";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useIsSample } from "@design-lab/shared/lib/config";
import { Card, CardContent } from "@design-lab/shared/ui/card";
import { Loader } from "@design-lab/shared/ui/loader";
import {
  artboardBackgroundAtom,
  mobileSizePlacementOpenAtom,
  RefineArtboard,
  refineArtworkOpenAtom,
  useArtboard
} from "@design-lab/widgets/artboard";

import { useSaveProject } from "@design-lab/features/save-project/hooks/useSaveProject";
import { useDesignLabArtworkAutoSizing } from "../model/useDesignLabArtworkAutoSizing";
import { ArtboardBase } from "./ArtboardBase";
import type { DesignLabArtboardProps } from "./ArtboardBase.types";

/**
 * Design Lab Artboard Wrapper
 *
 * Contains all Design Lab specific hooks, state management, and logic.
 * Passes data to ArtboardBase as props.
 */
// eslint-disable-next-line max-lines-per-function
export function DesignLabArtboard({ readonly = false }: DesignLabArtboardProps) {
  // Design Lab specific hooks
  const { form } = useArtworkForm();
  const { front, back } = useArtwork();
  const { deleteArtwork } = useArtworkImage();
  const { composition, placement } = useArtboard();
  const side = placement === "back" ? "back" : "front";
  const { source, artworkId } = useEnhancedImageSource({ side });
  const { isRestoring } = useDesignProjectSync();
  const { clearSelection } = useArtworkSelection();
  const isRefineOpen = useAtomValue(refineArtworkOpenAtom);
  const openRefine = useSetAtom(refineArtworkOpenAtom);
  const { isDesktop } = useResponsive();
  const isSample = useIsSample();

  const [backgroundType, setBackgroundType] = useAtom(artboardBackgroundAtom);
  const [isMobileSizePlacementOpen, setIsMobileSizePlacementOpen] = useAtom(
    mobileSizePlacementOpenAtom
  );
  const { isSaveProjectVisible } = useSaveProject();

  // Add this state and callbacks
  const [isProductBrowserOpen, setIsProductBrowserOpen] = useState(false);

  const handleOpenProductBrowser = useCallback(() => {
    setIsProductBrowserOpen(true);
  }, []);

  const handleCloseProductBrowser = useCallback(() => {
    setIsProductBrowserOpen(false);
  }, []);

  // Compositions for product carousel
  const { compositions } = useCompositionsList();
  const { currentComposition } = useCurrentComposition();
  const { selectCurrentComposition } = useCompositionsActions();

  const machine = side === "front" ? front : back;
  const surface = composition?.surfaces[placement];
  const frontSurface = composition?.surfaces?.front || composition?.surfaces?.frontLeftChest;
  const referenceBoundingBox =
    composition?.surfaces?.front?.boundingBox || composition?.surfaces?.frontLeftChest?.boundingBox;

  // Integrate fallback polling mechanism for BOTH front and back artworks
  const frontArtworkId = form.watch("front.artworkId");
  const backArtworkId = form.watch("back.artworkId");

  useArtworkWithPolling({
    artworkId: frontArtworkId ? Number(frontArtworkId) : undefined,
    startTime: front.state.context.startTime,
    machine: front,
    enabled: true
  });

  useArtworkWithPolling({
    artworkId: backArtworkId ? Number(backArtworkId) : undefined,
    startTime: back.state.context.startTime,
    machine: back,
    enabled: true
  });

  // Force form update when artwork finishes processing to trigger project save
  useEffect(() => {
    if (front.state.matches("finished") && front.state.context.artwork) {
      const currentArtworkId = form.getValues("front.artworkId");
      if (currentArtworkId) {
        // Touch the artwork ID field to trigger a form change and autosave
        form.setValue("front.artworkId", currentArtworkId, {
          shouldTouch: true,
          shouldDirty: true
        });
      }
    }
  }, [front.state, form]);

  useEffect(() => {
    if (back.state.matches("finished") && back.state.context.artwork) {
      const currentArtworkId = form.getValues("back.artworkId");
      if (currentArtworkId) {
        // Touch the artwork ID field to trigger a form change and autosave
        form.setValue("back.artworkId", currentArtworkId, { shouldTouch: true, shouldDirty: true });
      }
    }
  }, [back.state, form]);

  const artworkSizeBounds = useCalculatedArtworkDimensions(source.enhancedImage?.image, surface);

  // Auto-sizing logic
  useDesignLabArtworkAutoSizing({
    form,
    placement,
    surface,
    artworkImage: source.enhancedImage?.image,
    artworkSizeBounds
  });

  // Get all available surfaces for the current side
  const allSurfaces = useMemo(() => {
    const surfaces = [];

    if (placement === "back") {
      if (composition?.surfaces?.back)
        surfaces.push({ surface: composition.surfaces.back, placement });
    } else {
      if (composition?.surfaces?.front)
        surfaces.push({
          surface: composition.surfaces.front,
          placement: "front" as PlacementFormPosition
        });
      if (composition?.surfaces?.frontLeftChest)
        surfaces.push({
          surface: composition.surfaces.frontLeftChest,
          placement: "frontLeftChest" as PlacementFormPosition
        });
    }

    return surfaces;
  }, [composition?.surfaces, placement]);

  // Helper to create image preview from machine state
  const createImagePreview = useCallback((machineContext: typeof front.state.context) => {
    if (machineContext.file) {
      return toFileObject(machineContext.file, machineContext.originalImageUrl);
    }
    if (machineContext.artwork) {
      return { url: machineContext.artwork.originalImageUrl };
    }
    return undefined;
  }, []);

  // Image preview for loading boxes - separate for front and back
  const frontImagePreview = useMemo(
    () => createImagePreview(front.state.context),
    [
      createImagePreview,
      front.state.context.file,
      front.state.context.artwork,
      front.state.context.originalImageUrl
    ]
  );

  const backImagePreview = useMemo(
    () => createImagePreview(back.state.context),
    [
      createImagePreview,
      back.state.context.file,
      back.state.context.artwork,
      back.state.context.originalImageUrl
    ]
  );

  // Callbacks
  const onArtworkRemove = useCallback(() => {
    deleteArtwork(placement, artworkId);
    clearSelection(side);
  }, [deleteArtwork, placement, artworkId, clearSelection, side]);

  const handleCompositionSelect = useCallback(
    (id: string) => {
      selectCurrentComposition(id);
    },
    [selectCurrentComposition]
  );

  // Loading and feedback states
  const frontLoading = front.state.hasTag("loading");
  const backLoading = back.state.hasTag("loading");
  const isLoading = machine.state.hasTag("loading");
  const hasArtworkId = form.watch(`${side}.artworkId`);

  const LOADING_STEPS = [
    { caption: "Removing background" },
    { caption: "Enhancing resolution" },
    { caption: "Boosting vibrancy" },
    { caption: "Refining edges" }
  ];

  const createLoadingState = (
    loading: boolean,
    imagePreview: any,
    startTime: number | undefined
  ) =>
    loading
      ? {
          isLoading: true as const,
          imagePreview,
          startTime,
          estimatedDuration: ARTWORK_PROCESSING.ESTIMATED_DURATION,
          steps: LOADING_STEPS
        }
      : {
          isLoading: false,
          imagePreview,
          isFeedbackVisible: Boolean(machine.state.context.shouldShowLoadingFeedback)
        };

  const showArtworkFeedback =
    !isLoading &&
    source?.enhancementTier === "full_enhancements" &&
    !machine.state.matches({ finished: { feedback: "complete" } });

  // Show loading state while restoring design project or if composition is not ready
  if (isRestoring || !composition || !surface) {
    return <Loader message="Creating preview" />;
  }

  // Render Refine Artwork overlay shell
  if (isRefineOpen && surface && referenceBoundingBox && frontSurface) {
    return (
      <Card className="h-full flex-1 overflow-hidden rounded-none lg:rounded-xl">
        <CardContent className="relative h-full p-0">
          <RefineArtboard
            frontSurface={frontSurface}
            referenceBoundingBox={referenceBoundingBox}
            surface={surface}
            enhancedImage={source.enhancedImage}
            placement={placement}
          />
        </CardContent>
      </Card>
    );
  }

  // Prepare props for ArtboardBase
  const canvasProps = {
    surface,
    referenceBoundingBox,
    placement,
    artworkImage: isLoading ? undefined : source.enhancedImage?.image,
    onArtworkRemove: isSaveProjectVisible ? undefined : onArtworkRemove,
    frontSurface,
    allSurfaces,
    labType: "design" as const
  };

  const controls = {
    showSideSwitcher: true,
    showZoom: true,
    showBackgroundToggle: true,
    showPickAModel: true,
    showToolbar: isDesktop && !readonly,
    showProductBrowser: true, // Change this to true
    showRemoveBackground: false,
    showSizePlacement: true
  };

  const loadingState = createLoadingState(
    isLoading,
    side === "front" ? frontImagePreview : backImagePreview,
    machine.state.context.startTime
  );

  const frontLoadingState = createLoadingState(
    frontLoading,
    frontImagePreview,
    front.state.context.startTime
  );

  const backLoadingState = createLoadingState(
    backLoading,
    backImagePreview,
    back.state.context.startTime
  );

  const artworkFeedback = showArtworkFeedback
    ? {
        show: true,
        artworkState: machine.state,
        sendArtwork: machine.send
      }
    : undefined;

  const refineButton = {
    show: !isLoading,
    hasArtworkId: Boolean(hasArtworkId),
    onPress: () => openRefine(true)
  };

  const productCarousel = {
    show: true,
    compositions,
    selectedId: currentComposition?.id,
    onSelect: handleCompositionSelect,
    onAddMoreProduct: !isSample ? handleOpenProductBrowser : undefined
  };

  return (
    <ArtboardBase
      readonly={readonly}
      canvasProps={canvasProps}
      controls={controls}
      backgroundType={backgroundType}
      onBackgroundChange={setBackgroundType}
      frontLoadingState={frontLoadingState}
      backLoadingState={backLoadingState}
      currentSide={side}
      loadingState={loadingState}
      zoomConfig={{ initialZoom: 2, maxZoom: 10 }}
      artworkFeedback={artworkFeedback}
      refineButton={refineButton}
      productCarousel={productCarousel}
      mobileSizePlacementOpen={isMobileSizePlacementOpen}
      onMobileSizePlacementChange={setIsMobileSizePlacementOpen}
      isProductBrowserOpen={isProductBrowserOpen}
      onProductBrowserClose={handleCloseProductBrowser}
      isSaveProject={isSaveProjectVisible}
    />
  );
}
